import { ContainerApiSuccessResponse } from '../../containerApi.types'

// Matches the backend's SupportedLocales (go-backend-api/internal/data/models
// DesktopApp) and this app's own i18n languages (shared/constants/Languages).
export type DownloadableAppLocale =
  | 'en'
  | 'de'
  | 'fr'
  | 'es'
  | 'it'
  | 'pt'
  | 'zh'

// A translatable field, e.g. { en: "Icon", de: "Symbol" }. Locales without a
// translation are simply absent from the object.
export type LocalizedText = Partial<Record<DownloadableAppLocale, string>>

// A translatable list field (About paragraphs), e.g. { en: ["p1", "p2"] }.
export type LocalizedList = Partial<Record<DownloadableAppLocale, string[]>>

export type DownloadPlatformOs = 'windows' | 'mac' | 'linux'

export interface DownloadPlatform {
  os: DownloadPlatformOs
  label: string
  url: string
  size_bytes?: number
  is_signed?: boolean
  system_requirements?: string
}

type ChangelogNoteType = 'added' | 'changed' | 'fixed'

interface ChangelogNote {
  type: ChangelogNoteType
  text: LocalizedText
}

interface AppChangelogEntry {
  version: string
  date: string
  notes: ChangelogNote[]
}

export interface AppScreenshot {
  url: string
  title?: LocalizedText
  description?: LocalizedText
}

export interface AppVideo {
  url: string
  title?: LocalizedText
  description?: LocalizedText
}

interface AppFeature {
  icon: string
  title: LocalizedText
  description: LocalizedText
}

// Install steps are intentionally not translatable (backend keeps them
// plain text — see SupportedLocales comment in the Go model).
interface AppInstallStep {
  title: string
  description: string
}

export interface DownloadableApp {
  id: string
  slug: string
  name: string
  description?: LocalizedText
  about?: LocalizedList
  icon: string
  version?: string
  released_at?: string
  is_internal_only?: boolean
  screenshots?: AppScreenshot[]
  videos?: AppVideo[]
  features?: AppFeature[]
  install_steps?: AppInstallStep[]
  changelog?: AppChangelogEntry[]
  known_issues?: string[]
  known_issues_version?: string
  platforms: DownloadPlatform[]
}

export interface GetDownloadableAppsResponse
  extends ContainerApiSuccessResponse<DownloadableApp[]> {}

export interface GetDownloadableAppResponse
  extends ContainerApiSuccessResponse<DownloadableApp> {}
